import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Cpu, Battery, Activity, CloudSun } from 'lucide-react';

const TechnologyCard = ({ icon: Icon, title, description, specs }) => {
  return (
    <Card className="border-gray-100 hover:border-emerald-200 transition-all duration-300">
      <CardHeader>
        <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mb-4">
          <Icon className="w-7 h-7 text-emerald-700" />
        </div>
        <CardTitle className="text-xl font-semibold text-gray-900 mb-3">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 mb-4 leading-relaxed">{description}</p>
        <ul className="space-y-2">
          {specs.map((spec, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
              <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full mt-1.5 flex-shrink-0" />
              <span>{spec}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const Technology = () => {
  const technologies = [
    {
      icon: CloudSun,
      title: 'Solar Modules',
      description: 'High-efficiency photovoltaic panels designed for Indian climate conditions.',
      specs: [
        'Monocrystalline silicon technology',
        'Efficiency rating: 19-21%',
        'Anti-reflective coating for maximum light absorption',
        '25-year performance warranty'
      ]
    },
    {
      icon: Cpu,
      title: 'Inverters',
      description: 'Advanced power conversion systems ensuring optimal grid integration.',
      specs: [
        'String inverters with MPPT technology',
        '98%+ conversion efficiency',
        'Real-time performance monitoring',
        'Grid code compliant'
      ]
    },
    {
      icon: Battery,
      title: 'Energy Storage',
      description: 'Backup systems ensuring continuous power supply during peak demands.',
      specs: [
        'Lithium-ion battery technology',
        'Smart energy management system',
        'Load balancing capabilities',
        'Extended cycle life'
      ]
    },
    {
      icon: Activity,
      title: 'Monitoring Systems',
      description: 'Real-time tracking and analytics for performance optimization.',
      specs: [
        '24/7 remote monitoring',
        'Weather station integration',
        'Predictive maintenance alerts',
        'Performance analytics dashboard'
      ]
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology & Infrastructure</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            State-of-the-art solar technology designed for reliability and maximum energy yield
          </p>
        </div>

        {/* Technology Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {technologies.map((tech, index) => (
            <TechnologyCard key={index} {...tech} />
          ))}
        </div>

        {/* Technical Specifications Banner */}
        <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Plant Technical Specifications</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-700 mb-2">10 MW</div>
              <div className="text-sm text-gray-600">Total Capacity</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-700 mb-2">40,000+</div>
              <div className="text-sm text-gray-600">Solar Panels</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-700 mb-2">50 Acres</div>
              <div className="text-sm text-gray-600">Land Area</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-700 mb-2">15M+</div>
              <div className="text-sm text-gray-600">kWh/Year</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Technology;
